import { isGovernmentPayorOrder } from "../util/gearOrderUtil";

export const buildInitialTestSelectionForm = (gearOrder, activeGearRequisition, omsRequisition, userInfo) => {
	// Remove the "All Profiles" selection for Medicare orders
	let selectedPanel = activeGearRequisition?.panel ?? '';
	if (isGovernmentPayorOrder(gearOrder) && omsRequisition?.orderableItem?.mixedComponentPricing) {
		selectedPanel = '';
	}
	
	let components = activeGearRequisition?.components ?? [];
	const simplifiedClientBilling = gearOrder?.simplifiedClientBilling;
	const prcPaperless = !!(userInfo?.type === 'prc' && omsRequisition?.paperless);

	// Pre-select profile and components for paperless requisitions
	if ((prcPaperless || simplifiedClientBilling) && !selectedPanel && !components.length) {
		const selections = getPaperlessSelections(omsRequisition);
		selectedPanel = selections.selectedPanel;
		components = selections.components;
	}

	return {
		panel: {
			value: selectedPanel
		},
		components: {
			value: components
		},
		addOns: {
			value: activeGearRequisition?.addons ?? []
		}
	}
}

/**
 * Gets the pre-selected panel and components for a paperless requisition.
 * The first profile on the Orderable Item and all of that profile's components will be selected.
 * 
 * @param omsRequisition The OMS Requisition object containing the Orderable Item details
 * @return An object containing 'selectedPanel' and 'components' that should be selected
 */
export const getPaperlessSelections = (omsRequisition) => {
	let selectedPanel = '';
	let components = [];
	if (omsRequisition && omsRequisition.orderableItem && omsRequisition.orderableItem.profileList && omsRequisition.orderableItem.profileList.length) {
		selectedPanel = omsRequisition.orderableItem.profileList[0].id;
		if (omsRequisition.orderableItem.profileList[0].profileComponents) {
			components = omsRequisition.orderableItem.profileList[0].profileComponents.map(component => component.id + '');
		}
	}

	return {
		selectedPanel: selectedPanel,
		components: components
	}
}

/**
 * Crawls the profile component tree to generate an array of profile component ids.
 */
export const getProfileComponentIds = (profileComponents) => {
	if (!profileComponents?.length) {
		return [];
	}

	let componentIds = [];
	profileComponents.forEach((profileComponent) => {
		componentIds.push(profileComponent.id.toString());
		if (profileComponent.profileComponents?.length) {
			componentIds = componentIds.concat(getProfileComponentIds(profileComponent.profileComponents));
		}
	});

	return componentIds;
};

/**
 * Checks if the test selections have changed in such a way that could increase the price.
 * Pricing could be increased by changing to a different panel or adding new Add-Ons.
 * Because of the way Gear prices at a panel level, changing panel components or changing
 * between a partial and full panel will not affect the price.
 *
 * @param originalGearRequisition The previously saved GearRequisition, containing original test selections
 * @param currentTestSelectionForm The current, not yet saved, test selection details
 * @param omsRequisition The OMS Requisition structure containing all possible profiles, components, and add-ons
 */
export const isPossiblePriceIncrease = (originalGearRequisition, currentTestSelectionForm, omsRequisition) => {
	return (
		selectionContainsAdditionalAddons(originalGearRequisition, currentTestSelectionForm) ||
		hasSelectedProfileChanged(originalGearRequisition, currentTestSelectionForm, omsRequisition)
	);
};

/**
 * Checks if the current test selections form contains add-ons that are not present in the saved GearRequisition.
 */
const selectionContainsAdditionalAddons = (originalGearRequisition, currentTestSelectionForm) => {
	const selectedAddOns = currentTestSelectionForm?.addOns?.value?.map((value) => value) ?? [];
	const previousAddOns =
		originalGearRequisition?.addOnOptions?.map((addOn) => addOn.omsInputFieldID?.toString()) ?? [];

	return !selectedAddOns.every((addOn) => previousAddOns.includes(addOn));
};

/**
 * Checks if the selected Profile has changed.
 * This will also attempt to compare partial-panel selections (where the main profile is not selected but individual 
 * profile components are selected).
 */
const hasSelectedProfileChanged = (originalGearRequisition, currentTestSelectionForm, omsRequisition) => {
	// Use the selected main profile if available. Otherwise, determine the main profile from the selected components.
	let previousPanels = originalGearRequisition?.panel > 0
		? [originalGearRequisition.panel]
		: getPanelIdsForComponents(originalGearRequisition?.components, omsRequisition?.orderableItem?.profileList);

	let newPanels = currentTestSelectionForm?.panel?.value > 0
		? [currentTestSelectionForm.panel.value]
		: getPanelIdsForComponents(
				currentTestSelectionForm?.components?.value,
				omsRequisition?.orderableItem?.profileList
		);

	// This shouldn't happen, but handle the case where no Panel or Components are selected
	if (!newPanels?.length) {
		// Nothing selected now, price won't increase
		return false;
	} else if (!previousPanels?.length) {
		// Nothing previously selected but something is selected now, price will increase
		return true;
	}

	/**
	 * Things get complicated when finding panels by component Id because a component can be listed in multiple panels.
	 * If either list contains a single panel (Full-panel selected), check that the other list contains that panel.
	 * If both lists contain multiple panels, check that all panels from the original selections are included in the new selections.
	 */
	let sourceList, targetList;
	if (newPanels.length === 1) {
		sourceList = newPanels;
		targetList = previousPanels;
	} else {
		sourceList = previousPanels;
		targetList = newPanels;
	}

	// Check that all panels in sourceList are found in targetList
	return !sourceList.every((profileId) => targetList.includes(profileId));
};

/**
 * Finds all panels that contain components from the component list and returns an array of the panel Ids.
 * In other words, finds all possible parent panels for the given child components.
 * 
 * @param componentIdList A list of String panelComponent IDs
 * @param profileList An OMS Requisition profile list
 */
export const getPanelIdsForComponents = (componentIdList, profileList) => {
	if (!componentIdList?.length) {
		return [];
	}

	const panelIds = new Set();
	const profileMap = buildProfileComponentIdMap(profileList);
	componentIdList.forEach((componentId) => {
		for (const [key, value] of profileMap) {
			if (value?.find((id) => id === componentId)) {
				panelIds.add(key.toString());
			}
		}
	});

	// Use the spread syntax to return an array
	return [...panelIds];
};

/**
 * Build a map of all profiles and their child components.
 * The map key will be the profile ID.
 * The map value will be a list of all profileComponents contained in that profile. Nested 
 * profileComponents will be flattened and included in the value.
 * 
 * @param profileList An OMS Requisition profile list
 */
const buildProfileComponentIdMap = (profileList) => {
	const profileMap = new Map();
	if (!profileList?.length) {
		return profileMap;
	}

	profileList.forEach((profile) => {
		profileMap.set(profile.id, getProfileComponentIds(profile.profileComponents));
	});

	return profileMap;
};